import { type ComponentType, useCallback } from "react";

import { useShallow } from "zustand/react/shallow";

import { useGetTradingAccount } from "~/v2/api/hooks";
import { PortfolioHistoryWidget } from "~/v3-poc/features/portfolio-history/PortfolioHistoryWidget.connector";
import type { AtdWidgetGroupLinePosition } from "~/v3-poc/features/user-preferences";
import { useAtdPreferencesContext } from "~/v3-poc/features/user-preferences";
import { useGetWatchlist } from "~/v3-poc/hooks/useWatchlists";
import { ActivitiesWidget } from "../widgets/activities/ActivitiesWidget.connector";
import { BalancesWidget } from "../widgets/balances/BalancesWidget.connector";
import { ChartWidget } from "../widgets/chart/ChartWidget.connector";
import { OptionsChainWidget } from "../widgets/optionsChain/OptionsChainWidget.connector";
import { WorkspaceOrdersWidget } from "../widgets/orders/OrdersWidget.connector";
import { OrderTicketWidget } from "../widgets/orderTicket/OrderTicketWidget.connector";
import { PositionsWidget } from "../widgets/positions/PositionsWidget.connector";
import { WatchlistWidget } from "../widgets/watchlist/WatchlistWidget.connector";
import { useGroupSymbolMutation } from "./groups/useGroupSymbolMutation";
import { WIDGET_KIND_META, type WidgetKind } from "./widgetKind";
import {
  GROUP_COLOR_TO_RADIX,
  type RadixGroupColor,
} from "./workspace/groupColors";
import { useWorkspace, useWorkspaceStore } from "./workspace/Workspace.context";
import type { GroupId } from "./workspace/workspace.types";
import {
  getEffectiveWidgetSymbol,
  getWidgetTitleSymbol,
} from "./workspace/workspace.utils";

export interface WorkspaceWidgetControllerParams {
  id: string;
}

export interface WorkspaceWidgetBodyProps {
  id: string;
}

/** Kind → body connector. Kept here so `widgetKind` stays free of React imports. */
export const WIDGET_COMPONENTS: Record<
  WidgetKind,
  ComponentType<WorkspaceWidgetBodyProps>
> = {
  chart: ChartWidget,
  portfolioHistory: PortfolioHistoryWidget,
  balances: BalancesWidget,
  watchlist: WatchlistWidget,
  optionsChain: OptionsChainWidget,
  positions: PositionsWidget,
  orders: WorkspaceOrdersWidget,
  activities: ActivitiesWidget,
  orderTicket: OrderTicketWidget,
};

export interface ConnectedWorkspaceWidgetControllerValue {
  kind: WidgetKind;
  title: string;
  titleSymbolValue: string | undefined;
  titleSymbolError: string | undefined;
  titleEditorOn: boolean;
  accentColor: RadixGroupColor | undefined;
  groupLinePosition: AtdWidgetGroupLinePosition;
  showGroupSelect: boolean;
  WidgetBody: ComponentType<WorkspaceWidgetBodyProps>;
  onRemove: () => void;
  onTitleSymbolCommit: (symbol: string) => void;
}

export type WorkspaceWidgetControllerValue =
  | { kind: undefined }
  | ConnectedWorkspaceWidgetControllerValue;

/** Positions publishes "N open"; the chain already has a search field. */
const chromeOmitsSymbol = (kind: WidgetKind): boolean =>
  kind === "positions" || kind === "optionsChain";

const getWorkspaceWidgetTitle = ({
  kind,
  kindLabel,
  titleEditorOn,
  titleSymbol,
  watchlistName,
}: {
  kind: WidgetKind;
  kindLabel: string;
  titleEditorOn: boolean;
  titleSymbol: string | undefined;
  watchlistName: string | undefined;
}): string => {
  // Watchlist titles the gobroker list name, never the group's ticker — a
  // watchlist isn't a single-symbol view, so "Watchlist · AAPL" would be
  // misleading about what the widget shows.
  if (kind === "watchlist") {
    return watchlistName ?? WIDGET_KIND_META.watchlist.label;
  }

  if (titleEditorOn || chromeOmitsSymbol(kind)) {
    return kindLabel;
  }

  if (titleSymbol) {
    return `${kindLabel} · ${titleSymbol}`;
  }

  return kindLabel;
};

export const useWorkspaceWidgetController = ({
  id,
}: WorkspaceWidgetControllerParams): WorkspaceWidgetControllerValue => {
  const { widgetGroupLinePosition } = useAtdPreferencesContext();
  // Single useShallow subscription for all derived display state.
  const { kind, accentColor, symbol, titleSymbol, groupId, watchlistId } =
    useWorkspace(
      useShallow((s) => {
        const widget = s.widgets[id];
        if (!widget) {
          return {
            kind: undefined as WidgetKind | undefined,
            accentColor: undefined as RadixGroupColor | undefined,
            symbol: undefined as string | undefined,
            titleSymbol: undefined as string | undefined,
            groupId: null as GroupId | null,
            watchlistId: undefined as string | undefined,
          };
        }
        const group =
          widget.groupId !== null ? s.groups[widget.groupId] : undefined;
        return {
          kind: widget.kind as WidgetKind,
          accentColor: group
            ? GROUP_COLOR_TO_RADIX[group.colorSlot]
            : undefined,
          symbol: getEffectiveWidgetSymbol(s, id),
          titleSymbol: getWidgetTitleSymbol(s, id),
          groupId: widget.groupId,
          watchlistId: widget.watchlistId,
        };
      }),
    );

  // Actions obtained from vanilla store API — stable references, no subscription.
  const store = useWorkspaceStore();
  const { setGroupSymbol, groupSymbolError } = useGroupSymbolMutation(groupId);

  // Shares the body connector's detail cache entry — same account/watchlist
  // key — so the chrome title never issues a separate network request.
  const { account } = useGetTradingAccount();
  const { data: watchlistDetail } = useGetWatchlist({
    accountID: account?.id,
    watchlistId,
    options: {
      enabled: kind === "watchlist" && Boolean(account?.id && watchlistId),
      meta: { showErrorToast: false },
    },
  });

  const onRemove = useCallback(() => {
    store.getState().removeWidget(id);
  }, [store, id]);

  const onTitleSymbolCommit = useCallback(
    (next: string) => {
      if (groupId == null) {
        return;
      }
      setGroupSymbol(groupId, next);
    },
    [groupId, setGroupSymbol],
  );

  if (!kind) {
    return { kind: undefined };
  }

  const { isSingleSymbol, label: kindLabel } = WIDGET_KIND_META[kind];
  const titleEditorOn =
    isSingleSymbol && !chromeOmitsSymbol(kind) && groupId !== null;
  const title = getWorkspaceWidgetTitle({
    kind,
    kindLabel,
    titleEditorOn,
    titleSymbol,
    watchlistName: watchlistDetail?.name,
  });
  const WidgetBody = WIDGET_COMPONENTS[kind];

  return {
    kind,
    title,
    titleSymbolValue: titleSymbol ?? symbol,
    titleSymbolError: groupSymbolError,
    titleEditorOn,
    accentColor,
    groupLinePosition: widgetGroupLinePosition,
    showGroupSelect: isSingleSymbol,
    WidgetBody,
    onRemove,
    onTitleSymbolCommit,
  };
};
